import { CompanyConfigData } from 'custom';

/** Hapstar platform defaults — used when company-config theme fields are null/empty. */
export const HAPSTAR_PLATFORM_THEME_DEFAULTS = {
  themePrimaryCTAColor: '#019df2',
  themePrimaryCTAHoverColor: '#3fb9fc',
  /** Active nav pill / highlight only — not the white header bar. */
  themeNavBackgroundColor: '#fcd300',
  themeSurfaceBackgroundColor: '#fcfbf7',
  /** Action panel / bordered control outlines when company configures themeBorderColor. */
  themeBorderColor: '#beaaf3',
} as const;

export const PLATFORM_THEME_CSS_VARS = {
  primaryCta: '--hs-primary-cta',
  primaryCtaHover: '--hs-primary-cta-hover',
  navHighlight: '--hs-nav-highlight',
  surfaceBg: '--hs-surface-bg',
  borderColor: '--hs-theme-border',
} as const;

export type ResolvedPlatformTheme = {
  primaryCta: string;
  primaryCtaHover: string;
  navHighlight: string;
  surfaceBg: string;
  /** null when company-config themeBorderColor is unset — keep per-component defaults. */
  borderColor: string | null;
};

const resolveThemeColor = (value: string | null | undefined, fallback: string): string => {
  const trimmed = typeof value === 'string' ? value.trim() : '';

  return trimmed.length > 0 ? trimmed : fallback;
};

const resolveOptionalThemeColor = (value: string | null | undefined): string | null => {
  const trimmed = typeof value === 'string' ? value.trim() : '';

  return trimmed.length > 0 ? trimmed : null;
};

export const resolvePlatformTheme = (
  config?: Partial<CompanyConfigData> | null,
): ResolvedPlatformTheme => ({
  primaryCta: resolveThemeColor(
    config?.themePrimaryCTAColor,
    HAPSTAR_PLATFORM_THEME_DEFAULTS.themePrimaryCTAColor,
  ),
  primaryCtaHover: resolveThemeColor(
    config?.themePrimaryCTAHoverColor,
    HAPSTAR_PLATFORM_THEME_DEFAULTS.themePrimaryCTAHoverColor,
  ),
  navHighlight: resolveThemeColor(
    config?.themeNavBackgroundColor,
    HAPSTAR_PLATFORM_THEME_DEFAULTS.themeNavBackgroundColor,
  ),
  surfaceBg: resolveThemeColor(
    config?.themeSurfaceBackgroundColor,
    HAPSTAR_PLATFORM_THEME_DEFAULTS.themeSurfaceBackgroundColor,
  ),
  borderColor: resolveOptionalThemeColor(config?.themeBorderColor),
});

const readCssVar = (varName: string, fallback: string): string => {
  if (typeof document === 'undefined') return fallback;

  const value = getComputedStyle(document.documentElement).getPropertyValue(varName).trim();

  return value.length > 0 ? value : fallback;
};

const readOptionalCssVar = (varName: string): string | null => {
  if (typeof document === 'undefined') return null;

  const value = getComputedStyle(document.documentElement).getPropertyValue(varName).trim();

  return value.length > 0 ? value : null;
};

/** Reads the active platform theme from document CSS variables (falls back to Hapstar defaults). */
export const readPlatformThemeFromDocument = (): ResolvedPlatformTheme => ({
  primaryCta: readCssVar(
    PLATFORM_THEME_CSS_VARS.primaryCta,
    HAPSTAR_PLATFORM_THEME_DEFAULTS.themePrimaryCTAColor,
  ),
  primaryCtaHover: readCssVar(
    PLATFORM_THEME_CSS_VARS.primaryCtaHover,
    HAPSTAR_PLATFORM_THEME_DEFAULTS.themePrimaryCTAHoverColor,
  ),
  navHighlight: readCssVar(
    PLATFORM_THEME_CSS_VARS.navHighlight,
    HAPSTAR_PLATFORM_THEME_DEFAULTS.themeNavBackgroundColor,
  ),
  surfaceBg: readCssVar(
    PLATFORM_THEME_CSS_VARS.surfaceBg,
    HAPSTAR_PLATFORM_THEME_DEFAULTS.themeSurfaceBackgroundColor,
  ),
  borderColor: readOptionalCssVar(PLATFORM_THEME_CSS_VARS.borderColor),
});

export const hasCustomPlatformTheme = (config?: Partial<CompanyConfigData> | null): boolean =>
  [
    config?.themePrimaryCTAColor,
    config?.themePrimaryCTAHoverColor,
    config?.themeNavBackgroundColor,
    config?.themeSurfaceBackgroundColor,
    config?.themeBorderColor,
  ].some((value) => typeof value === 'string' && value.trim().length > 0);

/** Applies resolved theme to document CSS variables (does not touch dashboardBgColour). */
export const applyPlatformTheme = (config?: Partial<CompanyConfigData> | null): void => {
  const theme = resolvePlatformTheme(config);
  const root = document.documentElement;

  root.style.setProperty(PLATFORM_THEME_CSS_VARS.primaryCta, theme.primaryCta);
  root.style.setProperty(PLATFORM_THEME_CSS_VARS.primaryCtaHover, theme.primaryCtaHover);
  root.style.setProperty(PLATFORM_THEME_CSS_VARS.navHighlight, theme.navHighlight);
  root.style.setProperty(PLATFORM_THEME_CSS_VARS.surfaceBg, theme.surfaceBg);

  if (hasCustomPlatformTheme(config)) {
    root.setAttribute('data-hs-custom-platform-theme', '');
  } else {
    root.removeAttribute('data-hs-custom-platform-theme');
  }

  if (theme.borderColor) {
    root.setAttribute('data-hs-theme-border', '');
    root.style.setProperty(PLATFORM_THEME_CSS_VARS.borderColor, theme.borderColor);
  } else {
    root.removeAttribute('data-hs-theme-border');
    root.style.removeProperty(PLATFORM_THEME_CSS_VARS.borderColor);
  }
};
